<template>
  <modal :show.sync="internalShow" @close="handleClose" size="lg">
    <template #header>
      <div class="text-xl font-semibold">
        {{ isCreditNote ? 'Credit Note — Return Settlement' : 'Payment Details' }}
      </div>
    </template>

    <div class="payment-popup">
      <!-- Summary bar -->
      <div class="p-4 bg-gray-100 border-l-4 rounded-lg sale-summary"
           :class="isCreditNote ? 'border-red-500' : 'border-blue-500'">
        <h4 class="mb-2 font-bold">{{ isCreditNote ? 'Return Summary' : 'Sale Summary' }}</h4>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-5">
          <div class="flex flex-col">
            <strong>{{ isCreditNote ? 'Return Amount:' : 'Total:' }}</strong>
            <span :class="isCreditNote ? 'text-red-600' : 'text-blue-600'">
              {{ (isCreditNote ? Math.abs(rawTotal) : rawTotal) | currency }}
            </span>
          </div>
          <div class="flex flex-col">
            <strong>Discount:</strong>
            <span class="text-yellow-600">{{ discountAmount | currency }}</span>
          </div>
          <div class="flex flex-col">
            <strong>Round Off:</strong>
            <span class="text-gray-600">{{ roundOff | currency }}</span>
          </div>
          <div v-if="!isCreditNote" class="flex flex-col">
            <strong>Amount Due:</strong>
            <span class="text-green-600">{{ effectiveAmountDue | currency }}</span>
          </div>
          <div class="flex flex-col">
            <strong>Customer:</strong>
            <span>{{ customerName }}</span>
          </div>
        </div>
      </div>

      <!-- ===== CREDIT NOTE MODE ===== -->
      <div v-if="isCreditNote" class="mt-4">
        <h4 class="mb-3 font-bold">How should this return be settled?</h4>
        <div class="flex flex-col gap-3">
          <label class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer"
                 :class="creditNoteSettlement === 'wallet' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'">
            <input type="radio" v-model="creditNoteSettlement" value="wallet" class="mt-1" />
            <div>
              <div class="font-semibold">Add to Customer Wallet</div>
              <div class="text-sm text-gray-500">
                Credit {{ Math.abs(rawTotal) | currency }} to {{ customerName }}'s wallet balance.
              </div>
            </div>
          </label>
          <label class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer"
                 :class="creditNoteSettlement === 'cash' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'">
            <input type="radio" v-model="creditNoteSettlement" value="cash" class="mt-1" />
            <div>
              <div class="font-semibold">Cash Refund</div>
              <div class="text-sm text-gray-500">Record how the refund was given to the customer.</div>
            </div>
          </label>
        </div>

        <!-- Cash refund payment modes (only shown when cash is selected) -->
        <div v-if="creditNoteSettlement === 'cash'" class="mt-4">
          <h4 class="mb-2 font-bold">Refund Payment Details</h4>
          <div class="flex flex-row flex-wrap justify-start gap-2">
            <div
              v-for="paymentMode in sortedPaymentModes"
              :key="paymentMode.paymentModeId"
              class="flex flex-col p-2 justify-between min-w-[200px] bg-white rounded-lg border"
            >
              <div class="w-full font-medium mb-1">
                <a @click="handlePaymentModeClick(paymentMode)">{{ paymentMode.label }}</a>
              </div>
              <input
                :value="paymentAmounts[paymentMode.paymentModeId] || 0"
                @input="updatePaymentAmount(paymentMode.paymentModeId, $event.target.value)"
                type="number"
                min="0"
                class="w-full border border-gray-300 form-input"
                :placeholder="`${paymentMode.label} amount`"
              />
            </div>
          </div>
          <div class="p-3 mt-2 bg-gray-100 rounded-lg">
            <strong>Total Refunded: </strong>
            <span class="text-green-600">{{ totalPaid | currency }}</span>
          </div>
        </div>
      </div>

      <!-- ===== REGULAR SALE MODE ===== -->
      <div v-else class="mt-2 payment-modes">
        <!-- Wallet credit section -->
        <div v-if="walletBalance > 0"
             class="flex items-center gap-3 p-3 mb-3 bg-green-50 border border-green-300 rounded-lg">
          <input type="checkbox" id="applyWallet" v-model="applyWallet" />
          <label for="applyWallet" class="flex-1 cursor-pointer">
            <span class="font-semibold text-green-800">Apply Wallet Credit</span>
            <span class="ml-2 text-sm text-green-600">(Available: {{ walletBalance | currency }})</span>
          </label>
          <div v-if="applyWallet" class="flex items-center gap-1">
            <span class="text-sm text-gray-600">Use ₹</span>
            <input
              v-model.number="walletApplied"
              type="number"
              min="0"
              :max="maxWalletApply"
              class="w-28 border border-gray-300 rounded form-input text-sm"
              @input="clampWalletApplied"
            />
          </div>
        </div>

        <h4 class="mb-2 font-bold">Payment Details</h4>
        <div class="flex flex-row justify-between gap-2">
          <div
            v-for="paymentMode in sortedPaymentModes"
            :key="paymentMode.paymentModeId"
            class="flex flex-col p-2 justify-between min-w-[300px] bg-white rounded-lg border"
          >
            <div class="w-1/3 font-medium">
              <a @click="handlePaymentModeClick(paymentMode)">{{ paymentMode.label }}</a>
            </div>
            <div class="w-full">
              <input
                :value="paymentAmounts[paymentMode.paymentModeId] || 0"
                @input="updatePaymentAmount(paymentMode.paymentModeId, $event.target.value)"
                type="number"
                min="0"
                class="w-full border border-gray-300 form-input"
                :placeholder="`${paymentMode.label} amount`"
              />
            </div>
          </div>
        </div>

        <!-- Total Paid and Balance -->
        <div class="p-4 mt-2 bg-gray-100 rounded-lg payment-summary">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <strong>Paying: </strong>
              <span class="text-green-600">{{ totalPaid | currency }}</span>
              <span v-if="walletApplied > 0" class="ml-1 text-xs text-green-700">
                + {{ walletApplied | currency }} wallet
              </span>
            </div>
            <div>
              <strong>Balance: </strong>
              <span :class="balanceClass">{{ balance | currency }}</span>
            </div>
          </div>
          <div v-if="balance > 0"
               class="p-2 mt-2 text-yellow-800 bg-yellow-100 border border-yellow-400 rounded">
            <i class="mr-1 fas fa-exclamation-triangle"></i>
            <strong>Note:</strong> There is a pending balance of {{ balance | currency }}.
            The customer can pay this amount later.
          </div>
        </div>

        <!-- Payment Note -->
        <div class="mt-4 payment-note">
          <label class="block mb-1 font-medium">Payment Note</label>
          <textarea
            v-model="paymentNote"
            class="w-full border border-gray-300 rounded form-textarea"
            rows="3"
            placeholder="Enter any additional payment notes..."
          ></textarea>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end space-x-2">
        <button @click="handleClose" class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
          Cancel
        </button>
        <button
          v-if="!isCreditNote"
          type="button"
          @click="handlePrint"
          class="px-4 py-2 text-white bg-blue-500 rounded hover:bg-blue-600"
        >
          Print Receipt
        </button>
        <button
          type="button"
          :disabled="saving"
          @click="handleSave"
          :class="isCreditNote ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'"
          class="px-4 py-2 text-white rounded disabled:opacity-50"
        >
          <span v-if="saving" class="mr-2 fas fa-spinner fa-spin"></span>
          {{ saveLabel }}
        </button>
      </div>
    </template>
  </modal>
</template>

<script>
import { mapGetters } from "vuex";
import Modal from "@/components/Modal.vue";

export default {
  name: "PaymentPopup",
  components: { Modal },
  props: {
    show: { type: Boolean, default: false },
    mode: { type: String, default: "sale" }, // 'sale' | 'credit-note'
    walletBalance: { type: Number, default: 0 },
    totalAmount: { type: Number, default: 0 },
    rawTotal: { type: Number, default: 0 },
    discountMode: { type: String, default: "percent" },
    discount: { type: Number, default: 0 },
    roundOff: { type: Number, default: 0 },
    amountDue: { type: Number, default: 0 },
    customerName: { type: String, default: "" },
    selectedProducts: { type: Array, default: () => [] },
    saleData: { type: Object, default: () => ({}) },
    saving: { type: Boolean, default: false }
  },
  data() {
    return {
      paymentAmounts: {},
      paymentNote: "",
      internalShow: this.show,
      applyWallet: false,
      walletApplied: 0,
      creditNoteSettlement: "wallet"
    };
  },
  computed: {
    ...mapGetters({ paymentModeList: "paymentMode/getPaymentModes" }),
    isCreditNote() {
      return this.mode === "credit-note";
    },
    maxWalletApply() {
      return Math.min(this.walletBalance, Math.max(this.amountDue, 0));
    },
    effectiveAmountDue() {
      return Math.max(this.amountDue - (this.applyWallet ? this.walletApplied : 0), 0);
    },
    totalPaid() {
      return Object.values(this.paymentAmounts).reduce(
        (sum, amount) => sum + (parseFloat(amount) || 0),
        0
      );
    },
    balance() {
      return this.effectiveAmountDue - this.totalPaid;
    },
    balanceClass() {
      if (this.balance === 0) return "text-green-600 font-bold";
      if (this.balance > 0) return "text-orange-600 font-bold";
      return "text-red-600 font-bold";
    },
    discountAmount() {
      if (this.discountMode === "percent") {
        return Math.abs(this.rawTotal) * (this.discount / 100);
      }
      return this.discount;
    },
    saveLabel() {
      if (this.isCreditNote) {
        return this.creditNoteSettlement === "wallet" ? "Add to Wallet" : "Save Credit Note";
      }
      return "Save Sale";
    },
    sortedPaymentModes() {
      const order = ["UPI", "Cash", "Card", "Cheque", "Online"];
      return [...this.paymentModeList].sort((a, b) => {
        const ai = order.indexOf(a.label);
        const bi = order.indexOf(b.label);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return a.label.localeCompare(b.label);
      });
    }
  },
  watch: {
    show(newVal) {
      this.internalShow = newVal;
      if (newVal) {
        this.reset();
      }
    },
    internalShow(newVal) {
      if (!newVal) this.$emit("update:show", false);
    },
    applyWallet(val) {
      if (!val) this.walletApplied = 0;
      else this.walletApplied = this.maxWalletApply;
    }
  },
  methods: {
    reset() {
      this.paymentAmounts = {};
      this.paymentNote = "";
      this.applyWallet = false;
      this.walletApplied = 0;
      this.creditNoteSettlement = "wallet";
      this.paymentModeList.forEach(mode => {
        this.$set(this.paymentAmounts, mode.paymentModeId, 0);
      });
    },
    clampWalletApplied() {
      if (this.walletApplied > this.maxWalletApply) this.walletApplied = this.maxWalletApply;
      if (this.walletApplied < 0) this.walletApplied = 0;
    },
    updatePaymentAmount(modeId, value) {
      this.$set(this.paymentAmounts, modeId, parseFloat(value) || 0);
    },
    handleClose() {
      this.internalShow = false;
    },
    handlePrint() {
      this.$emit("print", this.getPaymentData());
    },
    handleSave() {
      this.$emit("save", this.getPaymentData());
    },
    handlePaymentModeClick(paymentMode) {
      const target = this.isCreditNote ? Math.abs(this.amountDue) : this.effectiveAmountDue;
      this.$set(this.paymentAmounts, paymentMode.paymentModeId, target);
    },
    getPaymentData() {
      const paymentBreakdown = Object.keys(this.paymentAmounts)
        .filter(modeId => this.paymentAmounts[modeId] > 0)
        .map(modeId => {
          const mode = this.paymentModeList.find(m => m.paymentModeId === modeId);
          return {
            paymentModeId: modeId,
            paymentModeLabel: mode ? mode.label : "",
            amount: parseFloat(this.paymentAmounts[modeId])
          };
        });

      return {
        totalPaid: this.totalPaid,
        balance: this.balance,
        paymentNote: this.paymentNote,
        paymentBreakdown,
        walletApplied: this.applyWallet ? this.walletApplied : 0,
        creditNoteSettlement: this.creditNoteSettlement,
        saleData: this.saleData,
        selectedProducts: this.selectedProducts
      };
    }
  },
  filters: {
    currency(value) {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount);
    }
  }
};
</script>

<style scoped>
.payment-popup {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
